/**
 * Enhanced API Service
 * Provides a clean interface for all API operations
 */
class ApiService {
  constructor() {
    this.baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
    this.vastuBaseURL = process.env.REACT_APP_VASTU_API_URL || 'http://localhost:5010/api';
    // this.baseURL = process.env.NODE_ENV === 'production'
    //   ? '/api'
    //   : process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
    this.timeout = 10000;
    // console.log("baseURL: ", this.baseURL)
  }

  // Build rich error objects while preserving backend metadata
  createApiError(message, status, data = {}) {
    const error = new Error(message);
    error.status = status;
    error.code = data.code;
    error.action = data.action;
    error.details = data;
    error.quotaLimit = data.quotaLimit;
    error.quotaUsed = data.quotaUsed;
    error.quotaRemainingToday = data.quotaRemainingToday;
    error.quotaResetTime = data.quotaResetTime;
    error.retryAfter = data.retryAfter;
    return error;
  }

  // Re-throw existing rich errors, or convert plain ones to Error objects
  rethrowError(error, fallbackMessage) {
    if (error instanceof Error) {
      if (!error.message) {
        error.message = fallbackMessage;
      }
      throw error;
    }

    const wrappedError = new Error(error?.message || fallbackMessage);
    if (error && typeof error === 'object') {
      Object.assign(wrappedError, error);
    }
    throw wrappedError;
  }

  // Get auth token from localStorage
  getAuthToken() {
    return localStorage.getItem('token');
  }

  // Get user data from localStorage
  getCurrentUserData() {
    const userData = localStorage.getItem('userData');
    return userData ? JSON.parse(userData) : null;
  }

  // Set user data in localStorage
  setUserData(userData) {
    localStorage.setItem('userData', JSON.stringify(userData));
  }

  // Clear auth data
  clearAuthData() {
    localStorage.removeItem('token');
    localStorage.removeItem('userData');

    // Keep AuthContext in sync when auth is invalidated from inside the API layer.
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('auth:session-cleared'));
    }
  }

  // Check if user is authenticated
  isAuthenticated() {
    const token = this.getAuthToken();
    const userData = this.getCurrentUserData();
    return !!(token && userData);
  }

  // Get request headers
  getHeaders(includeAuth = true) {
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    };

    if (includeAuth) {
      const token = this.getAuthToken();
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
    }

    return headers;
  }

  // Decide when a 401 should actually terminate the local session.
  // Not every 401 means the current token is invalid (e.g. missing header on a specific request).
  shouldClearAuthOn401(errorMessage = '', endpoint = '') {
    const message = String(errorMessage).toLowerCase();
    const path = String(endpoint).toLowerCase();

    if (path.startsWith('/auth/me')) return true;

    return (
      message.includes('token is not valid') ||
      message.includes('jwt') ||
      message.includes('expired') ||
      message.includes('user not found') ||
      message.includes('deactivated') ||
      message.includes('session expired')
    );
  }

  // Handle API response
  async handleResponse(response, context = {}) {
    let data;

    try {
      data = await response.json();
    } catch (error) {
      throw new Error('Invalid JSON response from server');
    }

    if (!response.ok) {
      const errorMessage = data?.message || `Request failed with status ${response.status}`;

      switch (response.status) {
        case 401:
          if (context.includeAuth && this.shouldClearAuthOn401(errorMessage, context.endpoint)) {
            this.clearAuthData();
          }
          throw this.createApiError(errorMessage || 'Session expired. Please login again.', response.status, data);
        case 403:
          throw this.createApiError(errorMessage || 'Access denied. You do not have permission to perform this action.', response.status, data);
        case 404:
          throw this.createApiError(errorMessage || 'Resource not found.', response.status, data);
        case 422:
          throw this.createApiError(errorMessage || 'Validation failed.', response.status, data);
        case 429:
          throw this.createApiError(errorMessage || 'Too many requests. Please try again later.', response.status, data);
        case 500:
          throw this.createApiError(errorMessage || 'Server error. Please try again later.', response.status, data);
        default:
          throw this.createApiError(errorMessage, response.status, data);
      }
    }

    return data;
  }

  // Generic request method
  async request(endpoint, options = {}) {
    const url = this.baseURL.startsWith('http') 
      ? `${this.baseURL}${endpoint}` 
      : `${this.baseURL}${endpoint.startsWith('/') ? '' : '/'}${endpoint}`;

    // Get default headers (includes Auth token and JSON content type)
    const headers = this.getHeaders(options.includeAuth !== false);

    // --- CRITICAL FIX FOR VAASTU FILE UPLOADS ---
    // If the payload is FormData (contains files), we MUST delete the manual Content-Type.
    // The browser will automatically set 'multipart/form-data; boundary=...' for us.
    if (options.body instanceof FormData) {
      delete headers['Content-Type'];
    }
    // --------------------------------------------

    const config = {
      method: 'GET', // Default method, will be overridden by options.method if provided
      headers: headers,
      ...options,
    };

    try {
      console.log(`🌐 Making request to: ${url}`);
      const response = await fetch(url, config);
      console.log(`📡 Response status: ${response.status}`);
      return await this.handleResponse(response);
    } catch (error) {
      console.error('❌ Request error:', error);
      
      if (error.message.includes('Failed to fetch')) {
        throw new Error(`Network error. Please check your connection to ${this.baseURL}`);
      }
      
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw new Error(`Cannot connect to backend at ${this.baseURL}. Please make sure the backend is running.`);
      }
      
      throw error;
    }
  }

  async requestExternal(baseURL, endpoint, options = {}) {
    const url = `${baseURL.replace(/\/$/, '')}${endpoint}`;
    const config = {
      method: 'GET',
      headers: this.getHeaders(options.includeAuth !== false),
      ...options,
    };

    const response = await fetch(url, config);
    return await this.handleResponse(response, {
      endpoint,
      includeAuth: options.includeAuth !== false,
    });
  }

  // --- Authentication methods ---

  async register(userData) {
    try {
      const response = await this.request('/auth/register', {
        method: 'POST',
        body: JSON.stringify(userData),
        includeAuth: false,
      });

      if (response.success && response.data.token) {
        localStorage.setItem('token', response.data.token);
        this.setUserData(response.data.user);
      }

      return {
        success: response.success,
        message: response.message,
        data: response.data,
        user: response.data?.user,
        token: response.data?.token
      };
    } catch (error) {
      throw new Error(error.message || 'Registration failed. Please try again.');
    }
  }

  async login(credentials) {
    try {
      const response = await this.request('/auth/login', {
        method: 'POST',
        body: JSON.stringify(credentials),
        includeAuth: false,
      });

      if (response.success && response.data.token) {
        localStorage.setItem('token', response.data.token);
        this.setUserData(response.data.user);
      }

      return {
        success: response.success,
        message: response.message,
        data: response.data,
        user: response.data?.user,
        token: response.data?.token
      };
    } catch (error) {
      throw new Error(error.message || 'Login failed. Please try again.');
    }
  }

  async requestPhoneLoginOtp(phone) {
    try {
      const response = await this.request('/auth/login/phone/request-otp', {
        method: 'POST',
        body: JSON.stringify({ phone }),
        includeAuth: false,
      });

      return {
        success: response.success,
        message: response.message,
        data: response.data
      };
    } catch (error) {
      throw new Error(error.message || 'Failed to send OTP. Please try again.');
    }
  }

  async verifyPhoneLoginOtp(payload) {
    try {
      const response = await this.request('/auth/login/phone/verify-otp', {
        method: 'POST',
        body: JSON.stringify(payload),
        includeAuth: false,
      });

      if (response.success && response.data.token) {
        localStorage.setItem('token', response.data.token);
        this.setUserData(response.data.user);
      }

      return {
        success: response.success,
        message: response.message,
        data: response.data,
        user: response.data?.user,
        token: response.data?.token
      };
    } catch (error) {
      throw new Error(error.message || 'OTP verification failed. Please try again.');
    }
  }

  async requestLoginOtp(identifier) {
    try {
      const response = await this.request('/auth/request-otp', {
        method: 'POST',
        body: JSON.stringify({ identifier }),
        includeAuth: false,
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to request OTP.');
    }
  }

  async verifyLoginOtp(identifier, otp) {
    try {
      const response = await this.request('/auth/verify-otp', {
        method: 'POST',
        body: JSON.stringify({ identifier, otp }),
        includeAuth: false,
      });

      // If successful, log the user in by saving the token and user data
      // just like the standard password login
      if (response.success && response.data.token) {
        localStorage.setItem('token', response.data.token);
        this.setUserData(response.data.user);
      }

      return {
        success: response.success,
        message: response.message,
        data: response.data,
        user: response.data?.user,
        token: response.data?.token
      };
    } catch (error) {
      throw new Error(error.message || 'Invalid or expired OTP.');
    }
  }

  async googleLogin(googleData) {
    try {
      const response = await this.request('/auth/google', {
        method: 'POST',
        body: JSON.stringify(googleData),
        includeAuth: false, // No auth token needed for login
      });

      // Handle success just like the regular login
      if (response.success && response.data.token) {
        localStorage.setItem('token', response.data.token);
        this.setUserData(response.data.user);
      }

      return {
        success: response.success,
        message: response.message,
        data: response.data,
        user: response.data?.user,
        token: response.data?.token
      };
    } catch (error) {
      throw new Error(error.message || 'Google login failed. Please try again.');
    }
  }

  async forgotPassword(email) {
    try {
      const response = await this.request('/auth/forgot-password', {
        method: 'POST',
        body: JSON.stringify({ email }),
        includeAuth: false,
      });
      return response; // Will return { success: true, message: '...' }
    } catch (error) {
      throw new Error(error.message || 'Failed to send reset email.');
    }
  }

  async resetPassword(token, password) {
    try {
      const response = await this.request(`/auth/reset-password/${token}`, {
        method: 'PUT',
        body: JSON.stringify({ password }),
        includeAuth: false,
      });

      // If successful, log the user in with the new token
      if (response.success && response.data.token) {
        localStorage.setItem('token', response.data.token);
      }
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to reset password.');
    }
  }

  // -----------------------------

  async getCurrentUser() {
    try {
      const response = await this.request('/auth/me');

      if (response.success && response.data.user) {
        this.setUserData(response.data.user);
      }

      return {
        success: response.success,
        data: response.data,
        user: response.data?.user
      };
    } catch (error) {
      this.clearAuthData();
      throw new Error(error.message || 'Failed to get user data.');
    }
  }

  async updateProfile(profileData) {
    try {
      const response = await this.request('/auth/profile', {
        method: 'PUT',
        body: JSON.stringify(profileData),
      });

      if (response.success && response.data.user) {
        this.setUserData(response.data.user);
      }

      return {
        success: response.success,
        message: response.message,
        data: response.data,
        user: response.data?.user
      };
    } catch (error) {
      throw new Error(error.message || 'Profile update failed. Please try again.');
    }
  }

  async logout() {
    try {
      // This request can fail if the token is already expired, which is fine.
      await this.request('/auth/logout', {
        method: 'POST',
      });
    } catch (error) {
      console.warn('Logout API call failed, but clearing data locally anyway.', error);
    } finally {
      this.clearAuthData();
    }
  }

  // --- Booking Method ---
  async createBooking(bookingData) {
    try {
      const response = await this.request('/bookings', {
        method: 'POST',
        body: JSON.stringify(bookingData),
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to create booking.');
    }
  }

  // --- Payment Methods ---
  async createRazorpayOrder(orderDetails) {
    try {
      const payload = {
        ...orderDetails,
        keyId: process.env.REACT_APP_RAZORPAY_KEY_ID,
      };

      const response = await this.request('/payment/create-order', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to create Razorpay order.');
    }
  }

  async verifyRazorpayPayment(paymentDetails) {
    try {
      const response = await this.request('/payment/verify-payment', {
        method: 'POST',
        body: JSON.stringify(paymentDetails),
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Payment verification failed.');
    }
  }

  // --- Health check ---
  async healthCheck() {
    try {
      const response = await this.request('/health', {
        includeAuth: false,
      });
      return {
        success: response.success,
        message: response.message,
        data: response
      };
    } catch (error) {
      throw new Error(error.message || 'Health check failed.');
    }
  }

  // --- For User Dashboard ---
  async getUserBookings() {
    try {
      // The endpoint matches the one we defined in our backend routes
      const response = await this.request('/bookings/my-bookings');
      return response; // Will return { success: true, data: [...] }
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch your bookings.');
    }
  }

  // --- For admin dashboard ---
  async getAllBookings(params = {}) {
    // This allows for future pagination/filtering, e.g., getAllBookings({ page: 2, status: 'confirmed' })
    try {
      const query = new URLSearchParams(params).toString();
      const response = await this.request(`/bookings?${query}`);
      return response; // Will return { success: true, data: [...], pagination: {...} }
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch all bookings.');
    }
  }

  async updateBookingStatus(bookingId, updateData) {
    // updateData will be an object like { status: 'completed', liveStreamLink: '...' }
    try {
      const response = await this.request(`/bookings/${bookingId}/status`, {
        method: 'PUT',
        body: JSON.stringify(updateData),
      });
      return response; // Will return { success: true, message: '...' }
    } catch (error) {
      throw new Error(error.message || 'Failed to update booking status.');
    }
  }

  async archiveBooking(bookingId, archiveData = {}) {
    try {
      const response = await this.request(`/bookings/${bookingId}/archive`, {
        method: 'POST',
        body: JSON.stringify(archiveData),
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to archive booking.');
    }
  }

  async getArchivedBookings(params = {}) {
    try {
      const query = new URLSearchParams(params).toString();
      const response = await this.request(`/bookings/archive${query ? `?${query}` : ''}`);
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch archived bookings.');
    }
  }

  async restoreArchivedBooking(archiveId) {
    try {
      const response = await this.request(`/bookings/archive/${archiveId}/restore`, {
        method: 'POST',
        body: JSON.stringify({}),
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to restore booking.');
    }
  }

  // --- Admin Product Store Methods ---
  // @desc    Get all products (Public)
  async getAllProducts() {
    try {
      const response = await this.request('/products');
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch products.');
    }
  }

  // @desc    Get a single product (Public)
  async getProductById(productId) {
    try {
      const response = await this.request(`/products/${productId}`);
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch product details.');
    }
  }

  async createProduct(productData) {
    try {
      // The 'protect' and 'authorize' middleware on the backend will handle security
      const response = await this.request('/products', {
        method: 'POST',
        body: JSON.stringify(productData),
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to create product.');
    }
  }

  async updateProduct(productId, productData) {
    try {
      const response = await this.request(`/products/${productId}`, {
        method: 'PUT',
        body: JSON.stringify(productData),
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to update product.');
    }
  }

  async deleteProduct(productId) {
    try {
      const response = await this.request(`/products/${productId}`, {
        method: 'DELETE',
      });
      return response; // Should return { success: true, message: 'Product removed' }
    } catch (error) {
      throw new Error(error.message || 'Failed to delete product.');
    }
  }

  async createStoreOrder(orderData) {
    try {
      const response = await this.request('/orders', {
        method: 'POST',
        body: JSON.stringify(orderData),
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to create order.');
    }
  }

  // For user dashboard
  async getMyOrders() {
    try {
      const response = await this.request('/orders/my-orders');
      return response; // Will return { success: true, data: [...] }
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch your orders.');
    }
  }

  // for admin dashboard
  async getAllOrders() {
    try {
      const response = await this.request('/orders');
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch all orders.');
    }
  }

  async updateOrderStatus(orderId, statusData) {
    // statusData should be an object like { status: 'Shipped' }
    try {
      const response = await this.request(`/orders/${orderId}/status`, {
        method: 'PUT',
        body: JSON.stringify(statusData),
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to update order status.');
    }
  }

  async archiveStoreOrder(orderId, archiveData = {}) {
    try {
      const response = await this.request(`/orders/${orderId}/archive`, {
        method: 'POST',
        body: JSON.stringify(archiveData),
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to archive order.');
    }
  }

  async getArchivedStoreOrders() {
    try {
      const response = await this.request('/orders/archive');
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch archived orders.');
    }
  }

  async restoreArchivedStoreOrder(archiveId) {
    try {
      const response = await this.request(`/orders/archive/${archiveId}/restore`, {
        method: 'POST',
        body: JSON.stringify({}),
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to restore order.');
    }
  }

  // --- Admin Dashboard Methods ---
  async getAdminDashboardStats() {
    try {
      const response = await this.request('/admin/stats');
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch dashboard stats.');
    }
  }

  async getAdminStats() {
    try {
      const response = await this.request('/admin/stats');
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch dashboard statistics.');
    }
  }

  // --- Admin Course Methods ---
  // == Courses ==
  async getAllCoursesAdmin() {
    try {
      const response = await this.request('/courses');
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch courses.');
    }
  }

  async createCourse(courseData) {
    try {
      const response = await this.request('/courses', {
        method: 'POST',
        body: JSON.stringify(courseData),
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to create course.');
    }
  }

  async updateCourse(courseId, courseData) {
    try {
      const response = await this.request(`/courses/${courseId}`, {
        method: 'PUT',
        body: JSON.stringify(courseData),
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to update course.');
    }
  }

  async deleteCourse(courseId) {
    try {
      const response = await this.request(`/courses/${courseId}`, {
        method: 'DELETE',
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to delete course.');
    }
  }

  async getCourseByIdForAdmin(courseId) {
    try {
      const response = await this.request(`/courses/${courseId}`);
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch course details.');
    }
  }

  // == Lessons ==
  async addLessonToModule(moduleId, lessonData) {
    try {
      const response = await this.request(`/courses/modules/${moduleId}/lessons`, {
        method: 'POST',
        body: JSON.stringify(lessonData),
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to add lesson.');
    }
  }

  async updateLesson(lessonId, lessonData) {
    try {
      const response = await this.request(`/courses/lessons/${lessonId}`, {
        method: 'PUT',
        body: JSON.stringify(lessonData),
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to update lesson.');
    }
  }

  async deleteLesson(lessonId) {
    try {
      const response = await this.request(`/courses/lessons/${lessonId}`, {
        method: 'DELETE',
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to delete lesson.');
    }
  }

  // == Modules ==
  async addModuleToCourse(courseId, moduleData) {
    try {
      const response = await this.request(`/courses/${courseId}/modules`, {
        method: 'POST',
        body: JSON.stringify(moduleData),
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to add module.');
    }
  }

  async updateModule(moduleId, moduleData) {
    try {
      const response = await this.request(`/courses/modules/${moduleId}`, {
        method: 'PUT',
        body: JSON.stringify(moduleData),
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to update module.');
    }
  }

  async deleteModule(moduleId) {
    try {
      const response = await this.request(`/courses/modules/${moduleId}`, {
        method: 'DELETE',
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to delete module.');
    }
  }

  // --- Public Course Methods ---
  async getAllPublishedCourses() {
    try {
      const response = await this.request('/courses/public');
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch courses.');
    }
  }

  async getPublicCourseDetails(courseId) {
    try {
      const response = await this.request(`/courses/public/${courseId}`);
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch course details.');
    }
  }

  // --- User Course Methods ---
  async enrollInFreeCourse(courseId) {
    try {
      const response = await this.request(`/courses/${courseId}/enroll`, {
        method: 'POST',
      });
      return response; // Will return { success: true, message: '...' }
    } catch (error) {
      throw new Error(error.message || 'Failed to enroll in course.');
    }
  }

  async enrollInPaidCourse(courseId) {
    try {
      const response = await this.request(`/courses/${courseId}/enroll-paid`, {
        method: 'POST',
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to enroll in paid course.');
    }
  }

  async getEnrolledCourse(courseId) {
    try {
      // This endpoint is protected and returns full course data for enrolled users
      const response = await this.request(`/courses/${courseId}/enrolled`);
      return response; // Will return { success: true, data: { course: {...}, enrollment: {...} } }
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch enrolled course content.');
    }
  }

  async markLessonAsComplete(lessonId) {
    try {
      const response = await this.request(`/courses/lessons/${lessonId}/complete`, {
        method: 'POST',
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to mark lesson as complete.');
    }
  }

  async getMyEnrollments() {
    try {
      const response = await this.request('/courses/my-enrollments');
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch your enrolled courses.');
    }
  }

  // --- Admin Catalogue Methods ---

  async getAllCatalogues() {
    try {
      const response = await this.request('/catalogues');
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch catalogues.');
    }
  }

  async createCatalogue(catalogueData) {
    try {
      const response = await this.request('/catalogues', {
        method: 'POST',
        body: JSON.stringify(catalogueData),
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to create catalogue.');
    }
  }

  async updateCatalogue(catalogueId, catalogueData) {
    try {
      const response = await this.request(`/catalogues/${catalogueId}`, {
        method: 'PUT',
        body: JSON.stringify(catalogueData),
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to update catalogue.');
    }
  }

  async deleteCatalogue(catalogueId) {
    try {
      const response = await this.request(`/catalogues/${catalogueId}`, {
        method: 'DELETE',
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to delete catalogue.');
    }
  }

  // --- Daily Spiritual Info / Panchang Methods ---

  async getCurrentDailySpiritualInfo() {
    try {
      const response = await this.request('/daily-spiritual-info/current', {
        includeAuth: false,
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch daily spiritual information.');
    }
  }

  async getAllDailySpiritualInfo() {
    try {
      const response = await this.request('/daily-spiritual-info');
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch daily spiritual information entries.');
    }
  }

  async createDailySpiritualInfo(payload) {
    try {
      const response = await this.request('/daily-spiritual-info', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to create daily spiritual information entry.');
    }
  }

  async updateDailySpiritualInfo(itemId, payload) {
    try {
      const response = await this.request(`/daily-spiritual-info/${itemId}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to update daily spiritual information entry.');
    }
  }

  async deleteDailySpiritualInfo(itemId) {
    try {
      const response = await this.request(`/daily-spiritual-info/${itemId}`, {
        method: 'DELETE',
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to delete daily spiritual information entry.');
    }
  }

  // --- Online Pooja Catalogue Methods ---

  async getPublicPoojaServices() {
    try {
      const response = await this.request('/pooja-services', {
        includeAuth: false,
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch pooja services.');
    }
  }

  async getPoojaAdminCatalogue() {
    try {
      const response = await this.request('/pooja-services/admin/catalogue');
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch pooja catalogue.');
    }
  }

  async updatePoojaService(serviceId, payload) {
    try {
      const response = await this.request(`/pooja-services/admin/services/${serviceId}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to update pooja service.');
    }
  }

  async createPoojaService(payload) {
    try {
      const response = await this.request('/pooja-services/admin/services', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to create pooja service.');
    }
  }

  async deletePoojaService(serviceId) {
    try {
      const response = await this.request(`/pooja-services/admin/services/${encodeURIComponent(serviceId)}`, {
        method: 'DELETE',
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to delete pooja service.');
    }
  }

  async updatePoojaTemple(templeId, payload) {
    try {
      const response = await this.request(`/pooja-services/admin/temples/${encodeURIComponent(templeId)}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to update temple.');
    }
  }

  async createPoojaTemple(payload) {
    try {
      const response = await this.request('/pooja-services/admin/temples', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to create temple.');
    }
  }

  async deletePoojaTemple(templeId) {
    try {
      const response = await this.request(`/pooja-services/admin/temples/${encodeURIComponent(templeId)}`, {
        method: 'DELETE',
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to delete temple.');
    }
  }

  // --- Bhakti Bal Chitra Katha Methods ---
  async getBalChitraKathaCategories() {
    try {
      const response = await this.request('/bhakti-bal-chitra-katha/categories', {
        includeAuth: false,
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch categories.');
    }
  }

  async getBalChitraKathaChannels(categoryId = '') {
    try {
      const query = categoryId ? `?categoryId=${encodeURIComponent(categoryId)}` : '';
      const response = await this.request(`/bhakti-bal-chitra-katha/channels${query}`, {
        includeAuth: false,
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch channels.');
    }
  }

  async getBalChitraKathaChannelById(channelId) {
    try {
      const response = await this.request(`/bhakti-bal-chitra-katha/channels/${channelId}`, {
        includeAuth: false,
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch channel details.');
    }
  }

  async getAdminBalChitraKathaChannels() {
    try {
      const response = await this.request('/bhakti-bal-chitra-katha/admin/channels');
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch admin channels.');
    }
  }

  async createAdminBalChitraKathaChannel(payload) {
    try {
      const response = await this.request('/bhakti-bal-chitra-katha/admin/channels', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to create channel.');
    }
  }

  async updateAdminBalChitraKathaChannel(channelId, payload) {
    try {
      const response = await this.request(`/bhakti-bal-chitra-katha/admin/channels/${channelId}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to update channel.');
    }
  }

  async deleteAdminBalChitraKathaChannel(channelId) {
    try {
      const response = await this.request(`/bhakti-bal-chitra-katha/admin/channels/${channelId}`, {
        method: 'DELETE',
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to delete channel.');
    }
  }

  // --- Bhakti Darshan Methods ---
  async getBhaktiDarshanShrines() {
    try {
      const response = await this.request('/bhakti-darshan/shrines', {
        includeAuth: false,
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch Bhakti Darshan shrines.');
    }
  }

  async getAdminBhaktiDarshanShrines() {
    try {
      const response = await this.request('/bhakti-darshan/admin/shrines');
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch Bhakti Darshan shrines.');
    }
  }

  async createAdminBhaktiDarshanShrine(payload) {
    try {
      const response = await this.request('/bhakti-darshan/admin/shrines', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to create Bhakti Darshan shrine.');
    }
  }

  async updateAdminBhaktiDarshanShrine(shrineId, payload) {
    try {
      const response = await this.request(`/bhakti-darshan/admin/shrines/${encodeURIComponent(shrineId)}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to update Bhakti Darshan shrine.');
    }
  }

  async deleteAdminBhaktiDarshanShrine(shrineId) {
    try {
      const response = await this.request(`/bhakti-darshan/admin/shrines/${encodeURIComponent(shrineId)}`, {
        method: 'DELETE',
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to delete Bhakti Darshan shrine.');
    }
  }

  // Bhakti jaapa methods
  async logJaapaCount(divineName, count, isMala = false) {
    try {
      const response = await this.request('/jaapa/log', {
        method: 'POST',
        body: JSON.stringify({ divineName, count, isMala }),
      });
      return response;
    } catch (error) {
      console.error('Failed to sync Jaapa log:', error);
      return { success: false };
    }
  }

  async getJaapaAnalytics(period = 'today') { // 'today', 'week', 'month', 'all'
    try {
      const response = await this.request(`/jaapa/analytics?period=${period}`);
      return response;
    } catch (error) {
      throw new Error(error.message || `Failed to fetch Jaapa analytics for ${period}.`);
    }
  }

  // --- Bhakti Samadhan (AI) Methods ---

  async sendAIMessage(message, conversationId = null, language = null) {
    try {
      const body = { message };
      if (conversationId) body.conversationId = conversationId;
      if (typeof language === 'string' && language.trim()) body.language = language.trim();
      const includeAuth = this.isAuthenticated();
      const response = await this.request('/ai/chat', {
        method: 'POST',
        body: JSON.stringify(body),
        includeAuth,
      });
      return response;
    } catch (error) {
      this.rethrowError(error, 'Failed to send message to Bhakti Samadhan.');
    }
  }

  async getAIConversations() {
    try {
      const response = await this.request('/ai/chat/conversations');
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch conversations.');
    }
  }

  async getAIConversation(conversationId) {
    try {
      const response = await this.request(`/ai/chat/conversations/${conversationId}`);
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch conversation.');
    }
  }

  async deleteAIConversation(conversationId) {
    try {
      const response = await this.request(`/ai/chat/conversations/${conversationId}`, {
        method: 'DELETE',
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to delete conversation.');
    }
  }

  async getAIRecommendations(type = 'all', language = null) {
    try {
      const includeAuth = this.isAuthenticated();
      const body = { type };
      if (typeof language === 'string' && language.trim()) body.language = language.trim();
      const response = await this.request(`/ai/recommendations/generate`, {
        method: 'POST',
        body: JSON.stringify(body),
        includeAuth,
      });
      return response;
    } catch (error) {
      this.rethrowError(error, 'Failed to get recommendations.');
    }
  }

  async getAIRecommendation(specificNeed, language = null) {
    try {
      const includeAuth = this.isAuthenticated();
      const body = { type: 'pooja', specificNeed };
      if (typeof language === 'string' && language.trim()) body.language = language.trim();
      const response = await this.request('/ai/recommendations/generate', {
        method: 'POST',
        body: JSON.stringify(body),
        includeAuth,
      });
      return response;
    } catch (error) {
      this.rethrowError(error, 'Failed to get recommendation.');
    }
  }

  async generateKundli(payload = {}) {
    try {
      const includeAuth = this.isAuthenticated();
      const response = await this.request('/ai/astrology/kundli/generate', {
        method: 'POST',
        body: JSON.stringify(payload),
        includeAuth,
      });
      return response;
    } catch (error) {
      this.rethrowError(error, 'Failed to generate kundli.');
    }
  }

  async analyzeKundli(payload = {}) {
    try {
      const includeAuth = this.isAuthenticated();
      const response = await this.request('/ai/astrology/kundli/analyze', {
        method: 'POST',
        body: JSON.stringify(payload),
        includeAuth,
      });
      return response;
    } catch (error) {
      this.rethrowError(error, 'Failed to analyze kundli.');
    }
  }

  async getAstrologyStatus() {
    try {
      const includeAuth = this.isAuthenticated();
      const response = await this.request('/ai/astrology/status', {
        method: 'GET',
        includeAuth,
      });
      return response;
    } catch (error) {
      this.rethrowError(error, 'Failed to fetch astrology status.');
    }
  }

  // We reuse the robust Consultant infrastructure. Astrologers/Tarot readers 
  // are simply managed as Consultants in the database and Admin Panel.

  async getSamadhanConsultants() {
    try {
      // Fetches all verified consultants. (You can later filter this to only show 
      // consultants who have 'Astrology' or 'Tarot' in their specializations)
      return await this.request('/consultants', { includeAuth: false });
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch spiritual guides.');
    }
  }

  async getSamadhanServicePlans() {
    try {
      return await this.request('/service-plans', { includeAuth: false });
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch service plans.');
    }
  }

  async createSamadhanConsultation(formData) {
    try {
      // Routes the booking to the unified consultations endpoint
      return await this.request('/consultations', {
        method: 'POST',
        body: formData,
      });
    } catch (error) {
      throw new Error(error.message || 'Failed to book consultation.');
    }
  }

  // --- Admin Enrollment Methods ---

  async getAllEnrollmentsAdmin() {
    try {
      const response = await this.request('/courses/enrollments');
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch enrollments.');
    }
  }

  async updateEnrollmentAdmin(enrollmentId, updateData) {
    try {
      const response = await this.request(`/courses/enrollments/${enrollmentId}`, {
        method: 'PUT',
        body: JSON.stringify(updateData),
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to update enrollment.');
    }
  }

  async deleteEnrollmentAdmin(enrollmentId) {
    try {
      const response = await this.request(`/courses/enrollments/${enrollmentId}`, {
        method: 'DELETE',
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to delete enrollment.');
    }
  }

  async adminEnrollUser(enrollmentData) {
    try {
      const response = await this.request('/courses/enrollments/admin-enroll', {
        method: 'POST',
        body: JSON.stringify(enrollmentData),
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to enroll user.');
    }
  }

  // ==========================================
  // VAASTU METHODS (MERGED)
  // ==========================================

  // --- Public & Booking Methods ---
  async getVastuConsultants() {
    return this.request('/consultants', { includeAuth: false });
  }

  async getVastuConsultantProfile(id) {
    return this.request(`/consultants/${id}/public`, { includeAuth: false });
  }

  async getVastuServicePlans() {
    return this.request('/service-plans', { includeAuth: false });
  }

  // Helper method for secure Vastu file downloads (Streaming via backend)
  async downloadVastuFile(endpoint, fileName = 'vastu-document.pdf') {
    const url = `${this.baseURL}${endpoint}`;
    // Force standard fetch instead of our wrapper because we need to handle Blobs, not JSON
    const response = await fetch(url, {
      headers: this.getHeaders(true),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.message || `File download failed with status ${response.status}`);
    }

    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
  }

  // --- User Consultation Methods ---
  async createVastuConsultation(formData) {
    // Note: formData automatically sets the correct Content-Type boundary in our overridden request method
    return this.request('/consultations', {
      method: 'POST',
      body: formData,
    });
  }

  async getMyVastuConsultations() {
    return this.request('/consultations/mine');
  }

  async getMyVastuConsultationById(consultationId) {
    return this.request(`/consultations/mine/${consultationId}`);
  }

  async uploadVastuConsultationFiles(consultationId, formData) {
    return this.request(`/consultations/${consultationId}/files`, {
      method: 'POST',
      body: formData,
    });
  }

  async submitPaymentProof(consultationId, formData) {
    return this.request(`/consultations/${consultationId}/payment-proof`, {
      method: 'POST',
      body: formData,
    });
  }

  async notifyVastuConsultantNewInfo(consultationId) {
    try {
      const response = await this.request(`/consultations/mine/${consultationId}/notify-review`, {
        method: 'PUT',
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to notify consultant.');
    }
  }

  // --- My Consultation (User) Payment Proof Methods ---
  async downloadMyPaymentQr(consultationId, fileName) {
    return this.downloadVastuFile(`/consultations/${consultationId}/payment/qr`, fileName);
  }

  async getMyPaymentQrPreviewUrl(consultationId) {
    // Streams the QR code direct from GCP and creates a local blob preview
    const token = this.getAuthToken();
    const url = `${this.baseURL}/consultations/${consultationId}/payment/qr`;
    
    // We must use fetch directly here to pass the Auth token for an image
    const response = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    
    if (!response.ok) throw new Error('Failed to load QR code preview');
    
    // Convert the secure response into a local browser URL
    const blob = await response.blob();
    return window.URL.createObjectURL(blob); 
  }

  async downloadMyVastuConsultationFile(consultationId, fileId, fileName) {
    return this.downloadVastuFile(`/consultations/${consultationId}/files/${fileId}`, fileName);
  }

  // --- Consultant Desk Methods ---
  async getVastuConsultantWork() {
    return this.request('/consultations/consultant/work');
  }

  async getMyVastuConsultantProfile() {
    return this.request('/consultants/me');
  }

  async updateVastuConsultantWork(consultationId, payload) {
    return this.request(`/consultations/consultant/work/${consultationId}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async updateConsultantPaymentInstructions(consultationId, formData) {
    return this.request(`/consultations/consultant/work/${consultationId}/payment-instructions`, {
      method: 'PUT',
      body: formData,
    });
  }

  async verifyConsultantPaymentProof(consultationId, payload) {
    return this.request(`/consultations/consultant/work/${consultationId}/payment-proof/verify`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async downloadConsultantPaymentAsset(consultationId, assetType, fileName) {
    return this.downloadVastuFile(`/consultations/consultant/work/${consultationId}/payment/${assetType}`, fileName);
  }

  async downloadConsultantFile(consultationId, fileId, fileName) {
    return this.downloadVastuFile(`/consultations/consultant/work/${consultationId}/files/${fileId}`, fileName);
  }

  // Compatibility alias for existing references
  async downloadVastuConsultantFile(consultationId, fileId, fileName) {
    return this.downloadConsultantFile(consultationId, fileId, fileName);
  }

  async addMyVastuConsultantFeeCategory(payload) {
    return this.request('/consultants/me/consultation-types', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  // --- Admin-Only Methods ---

  async assignVastuConsultantRole(email) {
    try {
      const response = await this.request('/auth/admin/vastu-consultant-role', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to assign Vastu consultant role.');
    }
  }

  async checkVastuConsultantAccess() {
    try {
      const response = await this.request('/consultants/access');
      return response;
    } catch (error) {
      return {
        success: false,
        data: {
          hasAccess: false,
        },
      };
    }
  }

  async getVastuAdminSummary() {
    try {
      const response = await this.request('/consultations/admin/summary');
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch Vastu summary.');
    }
  }

  async getVastuAdminConsultations(params = {}) {
    try {
      const query = new URLSearchParams(params).toString();
      const response = await this.request(`/consultations/admin${query ? `?${query}` : ''}`);
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch Vastu consultations.');
    }
  }

  async updateVastuAdminConsultation(consultationId, updateData) {
    try {
      const response = await this.request(`/consultations/admin/${consultationId}`, {
        method: 'PUT',
        body: JSON.stringify(updateData),
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to update Vastu consultation.');
    }
  }

  async downloadVastuAdminFile(consultationId, fileId, fileName = 'vastu-floor-plan') {
    return this.downloadVastuFile(`/consultations/admin/${consultationId}/files/${fileId}`, fileName);
  }

  async getVastuConsultantPortalConsultations() {
    try {
      const response = await this.request('/consultations/portal/consultant');
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch assigned Vastu consultations.');
    }
  }

  async updateVastuConsultantPortalConsultation(consultationId, updateData) {
    try {
      const response = await this.request(`/consultations/portal/consultant/${consultationId}`, {
        method: 'PUT',
        body: JSON.stringify(updateData),
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to update assigned Vastu consultation.');
    }
  }

  async downloadVastuConsultantPortalFile(consultationId, fileId, fileName = 'vastu-floor-plan') {
    return this.downloadVastuFile(`/consultations/portal/consultant/${consultationId}/files/${fileId}`, fileName);
  }

  async getVastuAdminConsultants() {
    try {
      const response = await this.request('/consultants/admin');
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch Vastu consultants.');
    }
  }

  async getVastuAdminServicePlans() {
    try {
      const response = await this.request('/service-plans/admin');
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to fetch Vastu fee categories.');
    }
  }

  async createVastuAdminServicePlan(payload) {
    try {
      const response = await this.request('/service-plans/admin', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to create Vastu fee category.');
    }
  }

  async updateVastuAdminServicePlan(planId, payload) {
    try {
      const response = await this.request(`/service-plans/admin/${planId}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to update Vastu fee category.');
    }
  }

  async createVastuAdminConsultant(payload) {
    try {
      const response = await this.request('/consultants/admin', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to create Vastu consultant.');
    }
  }

  async updateVastuAdminConsultant(consultantId, payload) {
    try {
      const response = await this.request(`/consultants/admin/${consultantId}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to update Vastu consultant.');
    }
  }

  // --- Vastu Admin Payment & Profile Photo Methods ---
  async updateVastuAdminConsultantProfile(consultantId, formData) {
    // Handles multi-part profile photo upload
    return this.request(`/consultants/admin/${consultantId}/profile`, {
      method: 'PUT',
      body: formData,
    });
  }

  async updateVastuAdminPaymentInstructions(consultationId, formData) {
    return this.request(`/consultations/admin/${consultationId}/payment-instructions`, {
      method: 'PUT',
      body: formData,
    });
  }

  async verifyVastuAdminPaymentProof(consultationId, payload) {
    return this.request(`/consultations/admin/${consultationId}/payment-proof/verify`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async downloadVastuAdminPaymentAsset(consultationId, assetType, fileName) {
    return this.downloadVastuFile(`/consultations/admin/${consultationId}/payment/${assetType}`, fileName);
  }

  async removeVastuConsultantFeeCategory(consultantId, typeId) {
    try {
      const response = await this.request(`/consultants/admin/${consultantId}/consultation-types/${typeId}`, {
        method: 'DELETE',
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to remove fee category.');
    }
  }

  // --- Vastu Consultant Portal Payment Methods ---
  async updateVastuConsultantPortalPaymentInstructions(consultationId, formData) {
    return this.request(`/consultations/portal/consultant/${consultationId}/payment-instructions`, {
      method: 'PUT',
      body: formData,
    });
  }

  async verifyVastuConsultantPortalPaymentProof(consultationId, payload) {
    return this.request(`/consultations/portal/consultant/${consultationId}/payment-proof/verify`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async downloadVastuConsultantPortalPaymentAsset(consultationId, assetType, fileName) {
    return this.downloadVastuFile(`/consultations/portal/consultant/${consultationId}/payment/${assetType}`, fileName);
  }

  // --- User Payment Screenshot Uploads ---
  async downloadMyPaymentQrPreviewUrl(consultationId) {
    // Streams the QR code direct from GCP and creates a local blob preview
    const token = this.getAuthToken();
    const url = `${this.baseURL}/consultations/${consultationId}/payment/qr`;
    const response = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) throw new Error('Failed to load QR code preview');
    const blob = await response.blob();
    return window.URL.createObjectURL(blob); // This returns a URL string suitable for <img src="..." />
  }

  async submitVastuReview(consultationId, payload) {
    try {
      const response = await this.request(`/consultations/${consultationId}/review`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      return response;
    } catch (error) {
      throw new Error(error.message || 'Failed to submit review.');
    }
  }

  async updateMyVastuDefaultPayment(formData) {
    return this.request('/consultants/me/default-payment', {
      method: 'PUT',
      body: formData,
    });
  }

  async addVastuConsultationMessage(consultationId, formData) {
    return this.request(`/consultations/${consultationId}/messages`, {
      method: 'POST',
      body: formData, // Uses FormData to support file attachments
    });
  }
}

// Create and export a singleton instance
const apiService = new ApiService();
export default apiService;